import { useState, useMemo } from 'react'
import { useDocuments, useStorageUsage, useDocumentMutations } from './hooks/useDocuments'
import { useMembers } from '../../hooks/useMembers'
import { useSubscription } from '../../hooks/useSubscription'
import { useAuth } from '../../hooks/useAuth'
import { FileTypeIcon, getFileType } from './components/FileTypeIcon'
import { DocumentUploadModal } from './components/DocumentUploadModal'
import { PdfViewer } from './components/PdfViewer'
import { SearchBar } from '../../components/ui/SearchBar'
import { cn } from '../../lib/utils'
import { memberColors } from '../../styles/tokens'
import { Portal } from '../../components/Portal'

const CATEGORIES = ['all','medical','school','legal','insurance','financial','vehicle','other']

function formatBytes(b) {
  if (!b) return '0 B'
  if (b < 1048576) return `${(b/1024).toFixed(0)} KB`
  if (b < 1073741824) return `${(b/1048576).toFixed(1)} MB`
  return `${(b/1073741824).toFixed(2)} GB`
}

// ── Document card ──────────────────────────────────────────────
const DocumentCard = ({ doc, members, onPreview, onDownload, onEdit, onDelete, isAdmin }) => {
  const [confirm, setConfirm] = useState(false)
  const uploader = doc.profiles?.display_name ?? 'Unknown'
  const color    = memberColors[doc.profiles?.member_color] ?? '#7c6faf'
  const assignee = members.find((m) => m.user_id === doc.member_id)
  const date     = new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const canPreview = doc.mime_type === 'application/pdf' || doc.mime_type?.startsWith('image/')

  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex items-start gap-3">
      <FileTypeIcon mimeType={doc.mime_type} size={44} />

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <p className="font-semibold text-text-primary text-sm truncate flex-1">{doc.title}</p>
          <span className="text-xs bg-border/60 text-text-muted px-2 py-0.5 rounded-full capitalize shrink-0">{doc.category}</span>
        </div>
        {doc.description && (
          <p className="text-xs text-text-secondary mt-0.5 line-clamp-1">{doc.description}</p>
        )}
        <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-text-muted">
          <span>{formatBytes(doc.file_size)}</span>
          <span>{date}</span>
          {assignee && (
            <span className="flex items-center gap-1">
              <span
                className="w-3 h-3 rounded-full inline-block"
                style={{ backgroundColor: memberColors[assignee.profiles?.member_color] ?? '#7c6faf' }}
              />
              {assignee.profiles?.display_name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: color }} />
            {uploader}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1.5 shrink-0">
        {canPreview && (
          <button onClick={() => onPreview(doc)}
            className="p-1.5 text-text-muted hover:text-primary rounded-lg hover:bg-primary/10"
            title="Preview">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
          </button>
        )}
        <button onClick={() => onDownload(doc)}
          className="p-1.5 text-text-muted hover:text-primary rounded-lg hover:bg-primary/10" title="Download">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
        </button>
        {(isAdmin || doc.uploaded_by === doc._selfId) && (
          <button onClick={() => onEdit(doc)}
            className="p-1.5 text-text-muted hover:text-text-secondary rounded-lg hover:bg-gray-100" title="Edit">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
          </button>
        )}
        {confirm ? (
          <div className="flex flex-col gap-1">
            <button onClick={() => setConfirm(false)} className="text-xs text-text-muted">Cancel</button>
            <button onClick={() => onDelete(doc)} className="text-xs text-red-500 font-semibold">Delete</button>
          </div>
        ) : (
          <button onClick={() => setConfirm(true)}
            className="p-1.5 text-text-muted hover:text-red-500 rounded-lg hover:bg-red-50" title="Delete">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
          </button>
        )}
      </div>
    </div>
  )
}

// ── Edit modal ─────────────────────────────────────────────────
const EditModal = ({ doc, members, onClose, onSave }) => {
  const [title,       setTitle]       = useState(doc.title)
  const [description, setDescription] = useState(doc.description ?? '')
  const [category,    setCategory]    = useState(doc.category)
  const [memberId,    setMemberId]    = useState(doc.member_id ?? '')
  const [saving,      setSaving]      = useState(false)

  return (
    <Portal>
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl w-full max-w-sm p-6 space-y-4">
        <h2 className="font-bold text-text-primary">Edit document</h2>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
          className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={2}
          className="w-full border border-border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-surface focus:outline-none capitalize">
          {['medical','school','legal','insurance','financial','vehicle','other'].map((c) =>
            <option key={c} value={c} className="capitalize">{c}</option>
          )}
        </select>
        <select value={memberId} onChange={(e) => setMemberId(e.target.value)}
          className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-surface focus:outline-none">
          <option value="">All members</option>
          {members.filter((m) => !m.isShared).map((m) =>
            <option key={m.user_id} value={m.user_id}>{m.profiles?.display_name}</option>
          )}
        </select>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-border rounded-xl py-3 text-sm">Cancel</button>
          <button onClick={async () => {
            setSaving(true)
            await onSave({ id: doc.id, title, description, category, memberId })
            setSaving(false)
            onClose()
          }} disabled={saving} className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}

// ── Image preview ──────────────────────────────────────────────
const ImagePreview = ({ url, fileName, onClose, onDownload }) => (
  <Portal>
  <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col">
    <div className="flex items-center justify-between px-4 py-3 bg-black/50 text-white shrink-0">
      <span className="text-sm truncate max-w-xs">{fileName}</span>
      <div className="flex gap-3">
        <button onClick={onDownload} className="text-white hover:text-gray-300">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
        </button>
        <button onClick={onClose} className="text-white hover:text-gray-300">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    </div>
    <div className="flex-1 overflow-auto flex items-center justify-center p-4">
      <img src={url} alt={fileName} className="max-w-full max-h-full object-contain rounded" />
    </div>
  </div>
  </Portal>
)

// ── Main page ──────────────────────────────────────────────────
export const DocumentsPage = () => {
  const { user, isAdmin, isLimited } = useAuth()
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [memberFilter,   setMemberFilter]   = useState('all')
  const [search,         setSearch]         = useState('')
  const [showUpload,     setShowUpload]      = useState(false)
  const [editDoc,        setEditDoc]         = useState(null)
  const [previewDoc,     setPreviewDoc]      = useState(null)
  const [previewUrl,     setPreviewUrl]      = useState(null)
  const [offline,        setOffline]         = useState(!navigator.onLine)

  const { data: documents = [], isLoading } = useDocuments({ category: categoryFilter, memberId: memberFilter })
  const { data: storageUsed = 0 }           = useStorageUsage()
  const { data: members = [] }              = useMembers()
  const { data: sub }                       = useSubscription()
  const { upload, update, remove, getUrl }  = useDocumentMutations()

  const isPaid = sub?.plan !== 'free'

  const filtered = useMemo(() => {
    if (!search.trim()) return documents
    const q = search.toLowerCase()
    return documents.filter((d) =>
      d.title?.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q) ||
      d.category?.toLowerCase().includes(q)
    )
  }, [documents, search])

  const handlePreview = async (doc) => {
    if (!navigator.onLine) {
      alert('Preview requires an internet connection.')
      return
    }
    try {
      const url = await getUrl(doc.file_path)
      setPreviewDoc(doc)
      setPreviewUrl(url)
    } catch {
      alert('Could not load preview.')
    }
  }

  const handleDownload = async (doc) => {
    if (!navigator.onLine) {
      alert('Downloads require an internet connection.')
      return
    }
    try {
      const url = await getUrl(doc.file_path)
      const a   = document.createElement('a')
      a.href    = url
      a.download = doc.file_name
      a.click()
    } catch {
      alert('Download failed.')
    }
  }

  const closePreview = () => { setPreviewDoc(null); setPreviewUrl(null) }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 page-enter">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Documents</h1>
        {!isLimited && (
          <button
            onClick={() => setShowUpload(true)}
            className="bg-primary text-white text-sm rounded-xl px-4 py-2.5 font-semibold flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Upload
          </button>
        )}
      </div>

      {/* Category filter */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-3 pb-1">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors capitalize',
              categoryFilter === c ? 'bg-primary text-white' : 'bg-background text-text-muted hover:bg-border/40'
            )}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Member filter chips */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-4 pb-1">
        <button
          onClick={() => setMemberFilter('all')}
          className={cn('px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors',
            memberFilter === 'all' ? 'bg-primary/20 text-primary font-medium' : 'bg-background text-text-muted')}
        >
          All members
        </button>
        {members.filter((m) => !m.isShared).map((m) => (
          <button
            key={m.user_id}
            onClick={() => setMemberFilter(m.user_id)}
            className={cn('px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors',
              memberFilter === m.user_id ? 'bg-primary/20 text-primary font-medium' : 'bg-background text-text-muted')}
          >
            {m.profiles?.display_name}
          </button>
        ))}
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search documents…" className="mb-4" />

      {isLoading && (
        <div className="space-y-3">
          {[1,2,3].map((i) => (
            <div key={i} className="bg-surface border border-border rounded-2xl p-4 flex gap-3 animate-pulse">
              <div className="w-11 h-11 rounded-xl bg-border" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-border rounded w-2/3" />
                <div className="h-3 bg-border rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-12 h-12 text-border mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <p className="text-text-muted">No documents yet.</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((doc) => (
          <DocumentCard
            key={doc.id}
            doc={{ ...doc, _selfId: user?.id }}
            members={members}
            isAdmin={isAdmin}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onEdit={setEditDoc}
            onDelete={(d) => remove.mutate({ id: d.id, filePath: d.file_path })}
          />
        ))}
      </div>

      {showUpload && (
        <DocumentUploadModal
          onClose={() => setShowUpload(false)}
          onUpload={(args) => upload.mutateAsync(args)}
          storageUsed={storageUsed}
          isPaid={isPaid}
        />
      )}

      {editDoc && (
        <EditModal
          doc={editDoc}
          members={members}
          onClose={() => setEditDoc(null)}
          onSave={(args) => update.mutateAsync(args)}
        />
      )}

      {previewDoc && previewUrl && (
        previewDoc.mime_type === 'application/pdf' ? (
          <PdfViewer
            url={previewUrl}
            fileName={previewDoc.file_name}
            onClose={closePreview}
            onDownload={() => handleDownload(previewDoc)}
          />
        ) : (
          <ImagePreview
            url={previewUrl}
            fileName={previewDoc.file_name}
            onClose={closePreview}
            onDownload={() => handleDownload(previewDoc)}
          />
        )
      )}
    </div>
  )
}
